import { API_SERVICE_ID } from "../../../helper/constants";
import { store } from "../../../redux/store";
import i18n from "i18next";

export const CALENDLY_ORIGIN = "https://calendly.com";
export const CALENDLY_EVENT_SCHEDULED = "calendly.event_scheduled";
export const SESSION_EXPIRED_ERROR_MESSAGE =
  "Session expired. Please restart registration.";

export const isCompleteBookingApiSuccess = (res) =>
  res?.status === true || res?.status === 1 || res?.status === "1";

export const isSessionExpiredError = (err) =>
  err?.message === SESSION_EXPIRED_ERROR_MESSAGE;

export const resolveRegistrationRdId = (selectedDietitian) =>
  selectedDietitian?.id ??
  selectedDietitian?.raw?.rd_id ??
  selectedDietitian?.raw?.id ??
  null;

export const getRegistrationTimezone = () =>
  Intl.DateTimeFormat().resolvedOptions().timeZone;

const parseNumericId = (value) => {
  if (value == null || value === "") {
    return null;
  }
  const parsed = Number(value);
  return Number.isNaN(parsed) ? value : parsed;
};

export const getRegistrationUserId = (registrationData) => {
  const fromStorage =
    registrationData?.userId ??
    registrationData?.signupResponse?.id ??
    registrationData?.signupResponse?.user?.id ??
    registrationData?.emailCodeCheck?.user?.id ??
    null;

  const resolved = parseNumericId(fromStorage);
  if (resolved != null) {
    return resolved;
  }

  return parseNumericId(store.getState().auth?.userId);
};

export const getRegistrationServiceId = (registrationData) => {
  let data = registrationData;
  if (!data || typeof data !== "object") {
    try {
      data = JSON.parse(localStorage.getItem("data") || "{}");
    } catch {
      data = {};
    }
  }

  const raw =
    data.service_id ??
    data.serviceId ??
    data.savePaymentTypeResponse?.service_id ??
    data.savePaymentTypeResponse?.serviceId ??
    data.signupResponse?.service_id ??
    data.emailCodeCheck?.service_id;

  const parsed = Number(raw);
  if (parsed === 1 || parsed === 2) {
    return parsed;
  }

  return API_SERVICE_ID;
};

export const buildCompleteBookingPayload = ({
  registrationData,
  rdId,
  calendlyEventPayload,
}) => {
  const parsedRdId = Number(rdId);

  return {
    user_id: getRegistrationUserId(registrationData),
    user_email: getRegistrationUserEmail(registrationData),
    rd_id: Number.isNaN(parsedRdId) ? rdId : parsedRdId,
    service_id: getRegistrationServiceId(),
    event_uri: calendlyEventPayload?.event?.uri ?? "",
    invitee_uri: calendlyEventPayload?.invitee?.uri ?? "",
  };
};

const CALENDLY_SLOT_DATETIME_SUFFIX = /\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}Z$/;

/** True when the URL targets a specific Calendly slot (datetime in path). */
export const hasCalendlyPreselectedSlotInUrl = (schedulingUrl) => {
  if (!schedulingUrl) {
    return false;
  }

  try {
    return CALENDLY_SLOT_DATETIME_SUFFIX.test(new URL(schedulingUrl).pathname);
  } catch {
    const path = String(schedulingUrl).split("?")[0];
    return CALENDLY_SLOT_DATETIME_SUFFIX.test(path);
  }
};

/**
 * Prefer rd-specific scheduling_url with a pre-selected slot datetime.
 * Generic booking_url links show the full Calendly intake (phone/SMS, etc.).
 */
export const resolvePreselectedSchedulingUrl = (rdInfo, startTime) => {
  if (!rdInfo || typeof rdInfo !== "object") {
    return "";
  }

  const directUrl = rdInfo.scheduling_url ?? rdInfo.schedulingUrl ?? "";
  if (directUrl && hasCalendlyPreselectedSlotInUrl(directUrl)) {
    return directUrl;
  }

  const bookingUrl =
    rdInfo.calendly_url?.booking_url ?? rdInfo.calendly_url?.bookingUrl ?? "";
  const slotStartTime =
    startTime ?? rdInfo.start_time ?? rdInfo.startTime ?? "";

  if (bookingUrl && slotStartTime) {
    const base = String(bookingUrl).replace(/\/$/, "");
    return `${base}/${slotStartTime}`;
  }

  return directUrl || bookingUrl;
};

export const buildCalendlyBookingUrl = (schedulingUrl, name, email) => {
  if (!schedulingUrl) {
    return "";
  }

  try {
    const url = new URL(schedulingUrl);
    if (name) {
      url.searchParams.set("name", name);
    }
    if (email) {
      url.searchParams.set("email", email);
    }
    return url.toString();
  } catch {
    const separator = schedulingUrl.includes("?") ? "&" : "?";
    const params = [];
    if (name) {
      params.push(`name=${encodeURIComponent(name)}`);
    }
    if (email) {
      params.push(`email=${encodeURIComponent(email)}`);
    }
    return params.length
      ? `${schedulingUrl}${separator}${params.join("&")}`
      : schedulingUrl;
  }
};

export const getRegistrationUserName = (registrationData) => {
  const firstName =
    registrationData?.firstName ??
    registrationData?.signupResponse?.first_name ??
    "";
  const lastName =
    registrationData?.lastName ??
    registrationData?.signupResponse?.last_name ??
    "";
  return `${firstName} ${lastName}`.trim();
};

export const getRegistrationUserEmail = (registrationData) =>
  registrationData?.email ??
  registrationData?.signupResponse?.email ??
  "";

const hasNonEmptyValue = (value) => {
  if (value == null || value === "") {
    return false;
  }
  if (Array.isArray(value)) {
    return value.some((item) => hasNonEmptyValue(item));
  }
  if (typeof value === "object") {
    return Object.values(value).some((item) => hasNonEmptyValue(item));
  }
  return true;
};

export const hasCalendlyAppointmentData = (data) => hasNonEmptyValue(data);

export const formatAppointmentDateTime = (appointment, fallback = "") => {
  const raw =
    appointment?.start_time ??
    appointment?.startTime ??
    appointment?.scheduled_at ??
    appointment?.scheduledAt ??
    null;

  if (!raw) {
    return fallback;
  }

  try {
    const date = new Date(raw);
    if (Number.isNaN(date.getTime())) {
      return fallback;
    }
    const lang = String(i18n.language || "en").split("-")[0];
    const locale = lang === "es" ? "es-ES" : "en-US";
    return date.toLocaleString(locale, {
      weekday: "long",
      month: "long",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return fallback;
  }
};

export const getAppointmentConfirmationNumber = (appointment) =>
  appointment?.confirmation_number ??
  appointment?.confirmationNumber ??
  appointment?.id ??
  null;

export const getAppointmentDietitianName = (appointment, fallback = "") =>
  appointment?.rd_name ??
  appointment?.rdName ??
  appointment?.dietitian_name ??
  appointment?.dietitianName ??
  fallback;
